import dayjs from 'dayjs';

import type { BedCategory } from 'app/typings';
import { PRICE_DIFFERENTIATION_MODE } from 'app/typings/core/common/openingConfiguration/priceConfigurationType';
import type {
  BookingDetails,
  BookingExperienceResponse,
  BookingRoomResponse,
} from 'app/typings/core/extranet/bookings/booking';
import { applyDiscountRate, multiplyPrice, sumPrices } from 'app/utils/pricing';
import { pluralize } from 'app/utils/strings';

import type { InfoRow } from '../../../components/commons/InfoBlock/InfoBlock';

export type PriceRow = {
  label: string;
  price: number;
  subRows?: PriceRow[];
  isDateIndicator?: boolean;
  isNightCount?: boolean;
  isOriginals?: boolean;
};

export type ExtraRow = PriceRow & {
  unitPrice: number;
  showUnitPrice: boolean;
  isOriginals: boolean;
};

const formatDate = (dateStr: string): string =>
  dayjs(dateStr).format('DD/MM/YYYY');

const getOpeningPrices = (
  opening: BookingExperienceResponse['openings'][number],
  isDaytime: boolean
): { price: number; childPrice?: number } =>
  isDaytime
    ? {
        price: opening.daytimePrice ?? 0,
        childPrice: opening.childDaytimePrice,
      }
    : { price: opening.price ?? 0, childPrice: opening.childPrice };

const isDaytimeBooking = (bookingDetails: BookingDetails): boolean =>
  dayjs(bookingDetails.checkOut)
    .startOf('day')
    .diff(dayjs(bookingDetails.checkIn).startOf('day'), 'day') === 0;

const isWithChildMode = (exp: BookingExperienceResponse): boolean =>
  exp.priceDifferentiationMode === PRICE_DIFFERENTIATION_MODE.WITH_CHILD ||
  exp.priceDifferentiationMode ===
    PRICE_DIFFERENTIATION_MODE.WITH_CHILD_DAYTIME;

const groupOpeningsByPrice = (
  exp: BookingExperienceResponse,
  isDaytime: boolean
): { adultPrice: number; childPrice: number; count: number }[] => {
  const groups = exp.openings.reduce((acc, opening) => {
    const prices = getOpeningPrices(opening, isDaytime);
    const adultPrice = prices.price;
    const childPrice = prices.childPrice ?? 0;
    const key = `${adultPrice}:${childPrice}`;
    const existing = acc.get(key);

    if (existing) {
      existing.count += 1;
    } else {
      acc.set(key, { adultPrice, childPrice, count: 1 });
    }

    return acc;
  }, new Map<string, { adultPrice: number; childPrice: number; count: number }>());

  return Array.from(groups.values());
};

const areAllPaxPricesEqual = (
  openings: BookingExperienceResponse['openings'],
  isDaytime: boolean
): boolean => {
  if (openings.length === 0) {
    return true;
  }

  const first = getOpeningPrices(openings[0], isDaytime);
  const firstEffectiveChildPrice = first.childPrice ?? first.price;

  return (
    first.price === firstEffectiveChildPrice &&
    openings.every((o) => {
      const { price, childPrice } = getOpeningPrices(o, isDaytime);

      return price === first.price && (childPrice ?? price) === first.price;
    })
  );
};

const getChargedAdults = (
  adults: number,
  children: number,
  minChargedPax?: number | null
): number => adults + Math.max(0, (minChargedPax ?? 0) - (adults + children));

const getIncludedExperienceBookedStock = (
  exp: BookingExperienceResponse,
  adults: number,
  children: number,
  minChargedPax?: number | null
): number => {
  const numOpenings = exp.openings.length;

  if (exp.isPerPerson) {
    const chargedAdults = getChargedAdults(adults, children, minChargedPax);

    return (
      exp.quantity *
      (chargedAdults + children) *
      (exp.isPerNight ? numOpenings : 1)
    );
  }

  return exp.quantity * (exp.isPerNight ? numOpenings : 1);
};

export const getExperienceTotal = (
  exp: BookingExperienceResponse,
  adults: number,
  children: number,
  minChargedPax?: number | null,
  isDaytime = false
): number => {
  if (exp.isPerPerson && (adults > 0 || children > 0)) {
    const chargedAdults = getChargedAdults(adults, children, minChargedPax);

    return sumPrices(
      exp.openings.flatMap((o) => {
        const { price, childPrice = 0 } = getOpeningPrices(o, isDaytime);

        return [
          multiplyPrice(
            applyDiscountRate(price, exp.packDiscount),
            chargedAdults
          ),
          multiplyPrice(
            applyDiscountRate(childPrice, exp.packDiscount),
            children
          ),
        ];
      })
    );
  }

  const quantity = exp.openings[0]?.bookedStock ?? 0;

  return multiplyPrice(
    sumPrices(
      exp.openings.map((o) =>
        applyDiscountRate(
          getOpeningPrices(o, isDaytime).price,
          exp.packDiscount
        )
      )
    ),
    quantity
  );
};

const getOriginalsTotal = (
  exp: BookingExperienceResponse,
  isDaytime: boolean
): number =>
  multiplyPrice(
    sumPrices(
      exp.openings.map(
        (o) => o.staycationPrice ?? getOpeningPrices(o, isDaytime).price
      )
    ),
    exp.quantity
  );

const buildPaxSubRows = (
  adults: number,
  children: number,
  adultPrice: number,
  childPrice: number,
  currencyFormatter: (price?: number | undefined) => string,
  numNights = 1
): PriceRow[] => [
  ...(adults > 0 && adultPrice > 0
    ? [
        {
          label: `${adults * numNights}x Adult price (${currencyFormatter(
            adultPrice
          )})`,
          price: multiplyPrice(adultPrice, adults * numNights),
        },
      ]
    : []),
  ...(children > 0 && childPrice > 0
    ? [
        {
          label: `${children * numNights}x Child price (${currencyFormatter(
            childPrice
          )})`,
          price: multiplyPrice(childPrice, children * numNights),
        },
      ]
    : []),
];

const getPerNightPaxSubRows = (
  exp: BookingExperienceResponse,
  adults: number,
  children: number,
  currencyFormatter: (price?: number | undefined) => string,
  minChargedPax?: number | null,
  isDaytime = false
): PriceRow[] => {
  const effectiveAdults = exp.isPerPerson
    ? getChargedAdults(adults, children, minChargedPax)
    : adults;

  return groupOpeningsByPrice(exp, isDaytime).flatMap(
    ({ adultPrice, childPrice, count }) => {
      const discountedAdultPrice = applyDiscountRate(
        adultPrice,
        exp.packDiscount
      );
      const discountedChildPrice =
        childPrice !== undefined && childPrice > 0
          ? applyDiscountRate(childPrice, exp.packDiscount)
          : children > 0
            ? discountedAdultPrice
            : 0;

      return [
        ...buildPaxSubRows(
          effectiveAdults,
          children,
          discountedAdultPrice,
          discountedChildPrice,
          currencyFormatter,
          count
        ),
        {
          label: `${pluralize(count, 'night', 'nights')} at this price`,
          price: 0,
          isNightCount: true,
          isOriginals: !!exp.countryId,
        },
      ];
    }
  );
};

const getPerOncePaxSubRows = (
  exp: BookingExperienceResponse,
  adults: number,
  children: number,
  currencyFormatter: (price?: number | undefined) => string,
  minChargedPax?: number | null,
  isDaytime = false
): PriceRow[] => {
  const effectiveAdults = exp.isPerPerson
    ? getChargedAdults(adults, children, minChargedPax)
    : adults;

  return exp.openings.flatMap((opening) => {
    const openingPrices = getOpeningPrices(opening, isDaytime);
    const adultPrice = applyDiscountRate(openingPrices.price, exp.packDiscount);
    const childPrice =
      openingPrices.childPrice !== undefined
        ? applyDiscountRate(openingPrices.childPrice, exp.packDiscount)
        : children > 0
          ? adultPrice
          : 0;

    return [
      ...buildPaxSubRows(
        effectiveAdults,
        children,
        adultPrice,
        childPrice,
        currencyFormatter
      ),
      ...(exp.isOpeningDateManuallySelected
        ? [
            {
              label: `Planned on ${formatDate(opening.date)}`,
              price: 0,
              isDateIndicator: true,
              isOriginals: !!exp.countryId,
            },
          ]
        : []),
    ];
  });
};

export const getIncludedPerNightExperiences = (
  bookingDetails: BookingDetails
): BookingExperienceResponse[] =>
  bookingDetails.experiences.filter(
    (exp) => exp.isIncluded && (exp.isPerNight || exp.isAlwaysIncludedWithNight)
  );

export const getIncludedPerOnceExperiences = (
  bookingDetails: BookingDetails
): BookingExperienceResponse[] =>
  bookingDetails.experiences.filter(
    (exp) => exp.isIncluded && !exp.isPerNight && !exp.isAlwaysIncludedWithNight
  );

export const getFreeIncludedPerNightRow = (
  bookingDetails: BookingDetails
): PriceRow | null => {
  const { adults, children, minChargedPax } = bookingDetails;
  const isDaytime = isDaytimeBooking(bookingDetails);
  const freeExps = getIncludedPerNightExperiences(bookingDetails).filter(
    (exp) =>
      getExperienceTotal(exp, adults, children, minChargedPax, isDaytime) === 0
  );

  if (freeExps.length === 0) {
    return null;
  }

  return {
    label: freeExps.map((exp) => exp.categoryName).join(' + '),
    price: 0,
  };
};

export const getPaidIncludedPerNightRows = (
  bookingDetails: BookingDetails,
  currencyFormatter: (price?: number | undefined) => string
): PriceRow[] => {
  const { adults, children, minChargedPax } = bookingDetails;
  const isDaytime = isDaytimeBooking(bookingDetails);

  return getIncludedPerNightExperiences(bookingDetails).flatMap((exp) => {
    const isOriginals = exp.isStaycationExperience;
    const price = isOriginals
      ? getOriginalsTotal(exp, isDaytime)
      : getExperienceTotal(exp, adults, children, minChargedPax, isDaytime);

    if (price === 0) {
      return [];
    }

    const stock = getIncludedExperienceBookedStock(
      exp,
      adults,
      children,
      minChargedPax
    );
    const suffix = isOriginals
      ? ` (Originals • ${currencyFormatter(price)})`
      : '';

    return [
      {
        label: `${stock}x ${exp.categoryName}${suffix}`,
        price,
        subRows: !areAllPaxPricesEqual(exp.openings, isDaytime)
          ? getPerNightPaxSubRows(
              exp,
              adults,
              children,
              currencyFormatter,
              minChargedPax,
              isDaytime
            )
          : undefined,
        isOriginals,
      },
    ];
  });
};

export const getIncludedPerOnceRows = (
  bookingDetails: BookingDetails,
  currencyFormatter: (price?: number | undefined) => string
): PriceRow[] => {
  const { adults, children, minChargedPax } = bookingDetails;
  const isDaytime = isDaytimeBooking(bookingDetails);

  return getIncludedPerOnceExperiences(bookingDetails).map((exp) => {
    const isOriginals = exp.isStaycationExperience;
    const total = isOriginals
      ? getOriginalsTotal(exp, isDaytime)
      : getExperienceTotal(exp, adults, children, minChargedPax, isDaytime);

    const bookedStock = getIncludedExperienceBookedStock(
      exp,
      adults,
      children,
      minChargedPax
    );
    const firstOpening = exp.openings[0];
    const unitPrice = applyDiscountRate(
      firstOpening ? getOpeningPrices(firstOpening, isDaytime).price : 0,
      exp.packDiscount
    );
    const label = isOriginals
      ? `${bookedStock}x ${exp.categoryName} (Originals • ${currencyFormatter(
          total
        )})`
      : exp.isPerPerson && !areAllPaxPricesEqual(exp.openings, isDaytime)
        ? `${bookedStock}x ${exp.categoryName}`
        : `${bookedStock}x ${exp.categoryName} (${currencyFormatter(unitPrice)})`;

    return {
      label,
      price: total,
      subRows:
        exp.isPerPerson && !areAllPaxPricesEqual(exp.openings, isDaytime)
          ? getPerOncePaxSubRows(
              exp,
              adults,
              children,
              currencyFormatter,
              minChargedPax,
              isDaytime
            )
          : undefined,
      isOriginals,
    };
  });
};

const getExtraTotal = (
  exp: BookingExperienceResponse,
  isDaytime: boolean
): number =>
  exp.openings.reduce((sum, o) => {
    if (exp.isStaycationExperience && o.staycationPrice !== undefined) {
      return sum + o.staycationPrice * o.bookedStock;
    }

    const { price, childPrice } = getOpeningPrices(o, isDaytime);

    if (o.adults !== undefined || o.children !== undefined) {
      return (
        sum + price * (o.adults ?? 0) + (childPrice ?? 0) * (o.children ?? 0)
      );
    }

    return sum + price * o.bookedStock;
  }, 0);

export const getExtraRows = (
  bookingDetails: BookingDetails,
  currencyFormatter: (price?: number | undefined) => string
): ExtraRow[] => {
  const isDaytime = isDaytimeBooking(bookingDetails);

  return bookingDetails.experiences.reduce<ExtraRow[]>((acc, exp) => {
    if (exp.isIncluded) {
      return acc;
    }

    const isOriginals = exp.isStaycationExperience;
    const firstOpening = exp.openings[0];
    const total = getExtraTotal(exp, isDaytime);
    const firstOpeningPrice = firstOpening
      ? getOpeningPrices(firstOpening, isDaytime).price
      : 0;

    const unitPrice = isOriginals
      ? (firstOpening?.staycationPrice ?? firstOpeningPrice)
      : firstOpeningPrice;

    if (!isOriginals && isWithChildMode(exp)) {
      const adults = firstOpening?.adults ?? 0;
      const children = firstOpening?.children ?? 0;

      acc.push({
        label: `${(adults + children) * exp.openings.length}x ${exp.categoryName}`,
        unitPrice,
        price: total,
        showUnitPrice: false,
        subRows: getPerOncePaxSubRows(
          exp,
          adults,
          children,
          currencyFormatter,
          undefined,
          isDaytime
        ),
        isOriginals: false,
      });
    } else {
      acc.push({
        label: `${firstOpening?.bookedStock ?? 0}x ${exp.categoryName}`,
        unitPrice,
        price: total,
        showUnitPrice: true,
        subRows: undefined,
        isOriginals,
      });
    }

    return acc;
  }, []);
};

export const getRoomTotal = (room: BookingRoomResponse): number =>
  room.openings.reduce((sum, o) => sum + (o.discountPrice ?? 0), 0);

const getAdditionalBedTotal = (
  beds: BookingRoomResponse['beds'],
  numNights: number
): number =>
  beds?.reduce(
    (sum, bed) =>
      sum + (bed.isIncluded || !bed.isSelected ? 0 : bed.price * numNights),
    0
  ) ?? 0;

const groupBedsByCategory = (
  beds: BookingRoomResponse['beds'],
  bedCategories: BedCategory[],
  numNights: number
): Map<string, { price: number; count: number }> => {
  const grouped = new Map<string, { price: number; count: number }>();

  beds?.forEach((bed) => {
    if (bed.isIncluded) {
      return;
    }

    const category = bedCategories.find((c) => c.id === bed.categoryId);
    const categoryName = category?.name || 'Unknown';
    const existing = grouped.get(categoryName);

    if (existing) {
      existing.count += 1;
    } else {
      grouped.set(categoryName, { price: bed.price, count: numNights });
    }
  });

  return grouped;
};

export const getRoomRows = (
  bookingDetails: BookingDetails,
  bedCategories: BedCategory[]
): PriceRow[] =>
  bookingDetails.rooms.flatMap((room) => {
    const nonIncludedBeds =
      room.beds?.filter((bed) => !bed.isIncluded && bed.isSelected === true) ??
      [];

    const numNights = dayjs(bookingDetails.checkOut).diff(
      bookingDetails.checkIn,
      'day'
    );
    const additionalBedTotal = getAdditionalBedTotal(
      nonIncludedBeds,
      numNights
    );

    const rows: PriceRow[] = [
      {
        label: room.name ?? room.categoryName,
        price: getRoomTotal(room),
      },
    ];

    if (nonIncludedBeds.length > 0 && numNights > 0) {
      const bedsByCategory = groupBedsByCategory(
        nonIncludedBeds,
        bedCategories,
        numNights
      );
      const subRows: PriceRow[] = Array.from(bedsByCategory.entries()).map(
        ([categoryName, { price, count }]) => ({
          label: `${count}x ${categoryName} (${price}€)`,
          price: count * price,
        })
      );

      rows.push({
        label: `${numNights}x Fee for additional bed in room`,
        price: additionalBedTotal,
        subRows,
      });
    }

    return rows;
  });

export const mapPriceSubRows = (
  subRows: {
    label: string;
    price: number;
    isDateIndicator?: boolean;
    isNightCount?: boolean;
  }[],
  currencyFormatter: (price?: number | undefined) => string
): InfoRow[] =>
  subRows.map((s) => {
    if (s.isDateIndicator) {
      return { label: s.label, variant: 'date' as const };
    }

    if (s.isNightCount) {
      return { label: s.label, variant: 'subheader' as const };
    }

    return { label: s.label, value: currencyFormatter(s.price) };
  });
